import React, { Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Row, Col, Card, Form, Button, Table, Spinner } from "react-bootstrap";
import PageTitle from "../../layouts/PageTitle";
import api from "../../../api";
import swal from "sweetalert";

const blankRow = () => ({ itemName: "", unit: "Nos", quantity: "", rate: "" });

const InventoryTransferForm = () => {
 const navigate = useNavigate();
 const [loading, setLoading] = useState(true);
 const [saving, setSaving] = useState(false);
 const [warehouses, setWarehouses] = useState([]);
 const [form, setForm] = useState({
 fromWarehouse: "",
 toWarehouse: "",
 remarks: "",
 items: [blankRow()],
 });

 useEffect(() => {
 const load = async () => {
 try {
 const [whRes] = await Promise.all([
 api.get("/warehouses?limit=500"),
 ]);
 if (whRes.data.success) setWarehouses(whRes.data.data || []);
 } catch (error) {
 swal("Error", "Failed to load transfer options", "error");
 } finally {
 setLoading(false);
 }
 };
 load();
 }, []);

 const handleChange = (e) => {
 const { name, value } = e.target;
 setForm((prev) => ({ ...prev, [name]: value }));
 };

 const handleItemChange = (index, name, value) => {
 setForm((prev) => {
 const nextItems = [...prev.items];
 nextItems[index] = { ...nextItems[index], [name]: value };
 return { ...prev, items: nextItems };
 });
 };

 const addRow = () => setForm((prev) => ({ ...prev, items: [...prev.items, blankRow()] }));
 const removeRow = (index) => {
 setForm((prev) => {
 const nextItems = prev.items.filter((_, i) => i !== index);
 return { ...prev, items: nextItems.length ? nextItems : [blankRow()] };
 });
 };

 const submit = async (e) => {
 e.preventDefault();
 if (!form.fromWarehouse || !form.toWarehouse) return swal("Warning", "Select source and destination warehouses", "warning");
 if (form.fromWarehouse === form.toWarehouse) return swal("Warning", "Source and destination warehouses must be different", "warning");
 if (!form.items.some((item) => Number(item.quantity) > 0)) return swal("Warning", "Add at least one item with quantity", "warning");

 try {
 setSaving(true);
 const payload = {
 fromWarehouse: form.fromWarehouse,
 toWarehouse: form.toWarehouse,
 remarks: form.remarks,
 items: form.items.map((item) => ({
 itemName: item.itemName,
 unit: item.unit || "Nos",
 quantity: Number(item.quantity || 0),
 rate: Number(item.rate || 0),
 })),
 };
 const res = await api.post("/inventory-stock/transfer", payload);
 if (res.data.success) {
 await swal("Success", "Stock transferred successfully", "success");
 navigate("/inventory-stock-ledger");
 }
 } catch (error) {
 swal("Error", error.response?.data?.message || "Failed to transfer stock", "error");
 } finally {
 setSaving(false);
 }
 };

 if (loading) {
 return (
 <div className="text-center py-5">
 <Spinner animation="border" />
 </div>
 );
 }

 return (
 <Fragment>
 <PageTitle activeMenu="Stock Transfer" motherMenu="Inventory" />
 <Row>
 <Col lg={12}>
 <Card>
 <Card.Header className="d-flex justify-content-between align-items-center">
 <Card.Title className="mb-0">Stock Transfer</Card.Title>
 <Button variant="secondary" onClick={() => navigate("/inventory-dashboard")}>Back</Button>
 </Card.Header>
 <Card.Body>
 <Form onSubmit={submit}>
 <Row className="g-3">
 <Col md={6}>
 <Form.Label>From Warehouse *</Form.Label>
 <Form.Select name="fromWarehouse" value={form.fromWarehouse} onChange={handleChange}>
 <option value="">Select Source</option>
 {warehouses.map((wh) => (
 <option key={wh._id} value={wh._id}>{wh.warehouseName}</option>
 ))}
 </Form.Select>
 </Col>
 <Col md={6}>
 <Form.Label>To Warehouse *</Form.Label>
 <Form.Select name="toWarehouse" value={form.toWarehouse} onChange={handleChange}>
 <option value="">Select Destination</option>
 {warehouses.map((wh) => (
 <option key={wh._id} value={wh._id}>{wh.warehouseName}</option>
 ))}
 </Form.Select>
 </Col>
 </Row>

 <div className="d-flex justify-content-between align-items-center mt-4 mb-2">
 <h6 className="mb-0">Items</h6>
 <Button type="button" variant="outline-primary" size="sm" onClick={addRow}>+ Add Row</Button>
 </div>

 <div className="table-responsive">
 <Table bordered className="align-middle">
 <thead>
 <tr>
 <th style={{ minWidth: 220 }}>Item</th>
 <th style={{ width: 130 }}>Unit</th>
 <th style={{ width: 120 }}>Qty</th>
 <th style={{ width: 140 }}>Rate</th>
 <th style={{ width: 120 }}>Actions</th>
 </tr>
 </thead>
 <tbody>
 {form.items.map((item, index) => (
 <tr key={index}>
 <td>
 <Form.Control value={item.itemName} onChange={(e) => handleItemChange(index, "itemName", e.target.value)} />
 </td>
 <td>
 <Form.Control value={item.unit} onChange={(e) => handleItemChange(index, "unit", e.target.value)} />
 </td>
 <td>
 <Form.Control type="number" min="0" value={item.quantity} onChange={(e) => handleItemChange(index, "quantity", e.target.value)} />
 </td>
 <td>
 <Form.Control type="number" min="0" value={item.rate} onChange={(e) => handleItemChange(index, "rate", e.target.value)} />
 </td>
 <td>
 <Button type="button" size="sm" variant="outline-danger" onClick={() => removeRow(index)}>Remove</Button>
 </td>
 </tr>
 ))}
 </tbody>
 </Table>
 </div>

 <Row className="mt-3">
 <Col md={12}>
 <Form.Label>Remarks</Form.Label>
 <Form.Control as="textarea" rows={3} name="remarks" value={form.remarks} onChange={handleChange} />
 </Col>
 </Row>

 <div className="d-flex justify-content-end gap-2 mt-4">
 <Button type="button" variant="light" onClick={() => navigate("/inventory-dashboard")}>Cancel</Button>
 <Button type="submit" disabled={saving}>{saving ? "Transferring..." : "Transfer Stock"}</Button>
 </div>
 </Form>
 </Card.Body>
 </Card>
 </Col>
 </Row>

 </Fragment>
 );
};

export default InventoryTransferForm;
